interface CornerNoteData {
    cornerNumber?: number;
    name?: string;
    entryDistPct?: number;
    apexDistPct?: number;
    exitDistPct?: number;
    approachNote?: string;
    entryNote?: string;
    apexNote?: string;
    exitNote?: string;
    enabled?: boolean;
    priority?: string;
    [key: string]: unknown;
}

interface CornerLabel {
    text?: string;
    distPct?: number;
}

interface TrackNotesData {
    trackId: string;
    trackName: string;
    corners: CornerNoteData[];
    approachOffset: number;
    entryOffset: number;
    apexOffset: number;
    exitOffset: number;
}

class TTSAgentClient {
    apiBase: string;
    endpointUrl: string;
    apiToken: string | null;
    connected: boolean;
    config: unknown;
    currentTrackNotes: unknown;

    constructor(options: { apiBase?: string; endpointUrl?: string; apiToken?: string } = {}) {
        this.apiBase = options.apiBase || '/api';
        this.endpointUrl = options.endpointUrl || 'http://localhost:8765';
        this.apiToken = options.apiToken || null;
        this.connected = false;
        this.config = null;
        this.currentTrackNotes = null;
    }

    setApiToken(token: string): void {
        this.apiToken = token;
    }

    getAuthHeaders(): Record<string, string> {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (this.apiToken) {
            headers['Authorization'] = `Bearer ${this.apiToken}`;
        }
        return headers;
    }

    // =========================================================================
    // Windows Endpoint Communication
    // =========================================================================

    async checkConnection(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/status`, {
                method: 'GET',
                mode: 'cors'
            });
            if (response.ok) {
                this.connected = true;
                return await response.json();
            }
        } catch {
            this.connected = false;
        }
        return { initialized: false, connected: false };
    }

    async getStatus(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/status`);
            return await response.json();
        } catch (error) {
            console.error('Failed to get TTS status:', error);
            return null;
        }
    }

    async getVoices(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/voices`);
            return await response.json();
        } catch (error) {
            console.error('Failed to get voices:', error);
            return { current: '', available: [] };
        }
    }

    async speak(text: string, priority: string = 'normal'): Promise<unknown> {
        try {
            const response = await fetch(
                `${this.endpointUrl}/tts/speak?text=${encodeURIComponent(text)}&priority=${priority}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to speak:', error);
            return { status: 'error', error: error instanceof Error ? error.message : String(error) };
        }
    }

    async stop(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/stop`, { method: 'POST' });
            return await response.json();
        } catch (error) {
            console.error('Failed to stop TTS:', error);
            return { status: 'error' };
        }
    }

    async pause(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/pause`, { method: 'POST' });
            return await response.json();
        } catch (error) {
            console.error('Failed to pause TTS:', error);
            return { status: 'error' };
        }
    }

    async resume(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/resume`, { method: 'POST' });
            return await response.json();
        } catch (error) {
            console.error('Failed to resume TTS:', error);
            return { status: 'error' };
        }
    }

    async enable(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/enable`, { method: 'POST' });
            return await response.json();
        } catch (error) {
            console.error('Failed to enable TTS:', error);
            return { status: 'error' };
        }
    }

    async disable(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/disable`, { method: 'POST' });
            return await response.json();
        } catch (error) {
            console.error('Failed to disable TTS:', error);
            return { status: 'error' };
        }
    }

    async getEndpointConfig(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/config`);
            this.config = await response.json();
            return this.config;
        } catch (error) {
            console.error('Failed to get endpoint config:', error);
            return null;
        }
    }

    async setEndpointConfig(config: Record<string, unknown>): Promise<unknown> {
        try {
            const params = new URLSearchParams();
            for (const [key, value] of Object.entries(config)) {
                params.append(key, String(value));
            }
            const response = await fetch(`${this.endpointUrl}/tts/config?${params}`, {
                method: 'POST'
            });
            this.config = await response.json();
            return this.config;
        } catch (error) {
            console.error('Failed to set endpoint config:', error);
            return null;
        }
    }

    async getEndpointNotes(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/notes`);
            this.currentTrackNotes = await response.json();
            return this.currentTrackNotes;
        } catch (error) {
            console.error('Failed to get endpoint notes:', error);
            return null;
        }
    }

    async setCornerNote(cornerNumber: number, note: Record<string, unknown>): Promise<unknown> {
        try {
            const params = new URLSearchParams();
            params.append('name', String(note.name || `Turn ${cornerNumber}`));
            params.append('entryDistPct', String(note.entryDistPct || 0));
            params.append('apexDistPct', String(note.apexDistPct || 0));
            params.append('exitDistPct', String(note.exitDistPct || 0));
            params.append('approachNote', String(note.approachNote || ''));
            params.append('entryNote', String(note.entryNote || ''));
            params.append('apexNote', String(note.apexNote || ''));
            params.append('exitNote', String(note.exitNote || ''));
            params.append('enabled', note.enabled !== false ? 'true' : 'false');
            params.append('priority', String(note.priority || 'normal'));

            const response = await fetch(
                `${this.endpointUrl}/tts/corner/${cornerNumber}?${params}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to set corner note:', error);
            return { status: 'error' };
        }
    }

    async clearEndpointNotes(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/notes/clear`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to clear notes:', error);
            return { status: 'error' };
        }
    }

    async saveEndpointNotesToFile(path: string = 'tts_notes.json'): Promise<unknown> {
        try {
            const response = await fetch(
                `${this.endpointUrl}/tts/notes/save?path=${encodeURIComponent(path)}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to save notes to file:', error);
            return { status: 'error' };
        }
    }

    async loadEndpointNotesFromFile(path: string = 'tts_notes.json'): Promise<unknown> {
        try {
            const response = await fetch(
                `${this.endpointUrl}/tts/notes/load?path=${encodeURIComponent(path)}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to load notes from file:', error);
            return { status: 'error' };
        }
    }

    async saveEndpointConfigToFile(path: string = 'tts_config.json'): Promise<unknown> {
        try {
            const response = await fetch(
                `${this.endpointUrl}/tts/config/save?path=${encodeURIComponent(path)}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to save config to file:', error);
            return { status: 'error' };
        }
    }

    async loadEndpointConfigFromFile(path: string = 'tts_config.json'): Promise<unknown> {
        try {
            const response = await fetch(
                `${this.endpointUrl}/tts/config/load?path=${encodeURIComponent(path)}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to load config from file:', error);
            return { status: 'error' };
        }
    }

    // =========================================================================
    // Backend Communication (Persistent Storage)
    // =========================================================================

    async getSavedNotes(trackId: string): Promise<unknown> {
        try {
            const response = await fetch(`${this.apiBase}/tts?action=notes&trackId=${trackId}`, {
                headers: this.getAuthHeaders()
            });
            const result = await response.json();
            return result.success ? result.data : null;
        } catch (error) {
            console.error('Failed to get saved notes:', error);
            return null;
        }
    }

    async saveNotes(trackNotes: unknown): Promise<unknown> {
        try {
            const response = await fetch(`${this.apiBase}/tts?action=save_notes`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(trackNotes)
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to save notes:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    async listTracks(): Promise<unknown[]> {
        try {
            const response = await fetch(`${this.apiBase}/tts?action=tracks`, {
                headers: this.getAuthHeaders()
            });
            const result = await response.json();
            return result.success ? result.tracks : [];
        } catch (error) {
            console.error('Failed to list tracks:', error);
            return [];
        }
    }

    async getSavedConfig(): Promise<unknown> {
        try {
            const response = await fetch(`${this.apiBase}/tts?action=config`, {
                headers: this.getAuthHeaders()
            });
            const result = await response.json();
            return result.success ? result.config : null;
        } catch (error) {
            console.error('Failed to get saved config:', error);
            return null;
        }
    }

    async saveConfig(config: unknown): Promise<unknown> {
        try {
            const response = await fetch(`${this.apiBase}/tts?action=save_config`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(config)
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to save config:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    async syncToEndpoint(trackId: string): Promise<unknown> {
        try {
            const response = await fetch(`${this.apiBase}/tts?action=sync`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    trackId: trackId,
                    endpointUrl: this.endpointUrl
                })
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to sync to endpoint:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // =========================================================================
    // API Token Management
    // =========================================================================

    async verifyToken(): Promise<unknown> {
        if (!this.apiToken) {
            return { success: false, valid: false, error: 'No token set' };
        }

        try {
            const response = await fetch(`${this.apiBase}/token?action=verify`, {
                headers: this.getAuthHeaders()
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to verify token:', error);
            return { success: false, valid: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    async setEndpointToken(token: string, serverUrl: string | null = null): Promise<unknown> {
        try {
            let url = `${this.endpointUrl}/tts/auth/token?token=${encodeURIComponent(token)}`;
            if (serverUrl) {
                url += `&server=${encodeURIComponent(serverUrl)}`;
            }
            const response = await fetch(url, { method: 'POST' });
            return await response.json();
        } catch (error) {
            console.error('Failed to set endpoint token:', error);
            return { status: 'error', error: error instanceof Error ? error.message : String(error) };
        }
    }

    async getEndpointAuthStatus(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/tts/auth/status`);
            return await response.json();
        } catch (error) {
            console.error('Failed to get endpoint auth status:', error);
            return { hasToken: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    // =========================================================================
    // Convenience Methods
    // =========================================================================

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async loadAndSyncTrack(trackId: string): Promise<boolean> {
        const notes: any = await this.getSavedNotes(trackId);
        if (!notes) return false;

        await this.clearEndpointNotes();

        await this.setEndpointConfig({
            trackName: notes.trackName,
            trackId: notes.trackId
        });

        for (const corner of notes.corners || []) {
            await this.setCornerNote(corner.cornerNumber, corner);
        }

        return true;
    }

    createCornerNote(cornerNumber: number, entryPct: number = 0): CornerNoteData {
        return {
            cornerNumber: cornerNumber,
            name: `Turn ${cornerNumber}`,
            entryDistPct: entryPct,
            apexDistPct: entryPct + 0.01,
            exitDistPct: entryPct + 0.02,
            approachNote: '',
            entryNote: '',
            apexNote: '',
            exitNote: '',
            enabled: true,
            priority: 'normal'
        };
    }

    buildTrackNotes(cornerLabels: CornerLabel[], trackName: string, trackId: number | string): TrackNotesData {
        const corners = cornerLabels.map((label, index) => ({
            cornerNumber: index + 1,
            name: label.text || `Turn ${index + 1}`,
            entryDistPct: label.distPct || 0,
            apexDistPct: (label.distPct || 0) + 0.01,
            exitDistPct: (label.distPct || 0) + 0.02,
            approachNote: '',
            entryNote: '',
            apexNote: '',
            exitNote: '',
            enabled: true,
            priority: 'normal'
        }));

        return {
            trackId: String(trackId),
            trackName: trackName,
            corners: corners,
            approachOffset: 3.0,
            entryOffset: 1.5,
            apexOffset: 0.5,
            exitOffset: 0.0
        };
    }

    // =========================================================================
    // Coaching Integration
    // =========================================================================

    async enableCoaching(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/coaching/enable`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to enable coaching:', error);
            return { status: 'error', error: error instanceof Error ? error.message : String(error) };
        }
    }

    async disableCoaching(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/coaching/disable`, {
                method: 'POST'
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to disable coaching:', error);
            return { status: 'error', error: error instanceof Error ? error.message : String(error) };
        }
    }

    async syncCoachingNotes(notes: unknown): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/coaching/sync_notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(notes)
            });
            return await response.json();
        } catch (error) {
            console.error('Failed to sync coaching notes:', error);
            return { status: 'error', error: error instanceof Error ? error.message : String(error) };
        }
    }

    async announceCoaching(text: string, priority: string = 'normal'): Promise<unknown> {
        try {
            const response = await fetch(
                `${this.endpointUrl}/coaching/announce?text=${encodeURIComponent(text)}&priority=${priority}`,
                { method: 'POST' }
            );
            return await response.json();
        } catch (error) {
            console.error('Failed to announce coaching:', error);
            return { status: 'error', error: error instanceof Error ? error.message : String(error) };
        }
    }

    async getCoachingStatus(): Promise<unknown> {
        try {
            const response = await fetch(`${this.endpointUrl}/coaching/status`);
            return await response.json();
        } catch (error) {
            console.error('Failed to get coaching status:', error);
            return { ttsEnabled: false, connected: false };
        }
    }
}

window.TTSAgentClient = TTSAgentClient;
